import type { JSONContent } from "@tiptap/react";

/*
 * The review page edits the brief's plain text in tiptap but must store plain
 * text, never HTML — `briefTextToHtml` (shared) renders it into the editor and
 * this serializes the editor's document back. Bold and italic are the only
 * marks kept.
 */

function inlineText(node: JSONContent): string {
  if (node.type === "text") {
    const text = node.text ?? "";

    if (!text.trim()) return text;
    const hasMark = (type: string) =>
      node.marks?.some((mark) => mark.type === type);
    const italic = hasMark("italic") ? `_${text}_` : text;

    return hasMark("bold") ? `**${italic}**` : italic;
  }
  if (node.type === "hardBreak") return "\n";

  return (node.content ?? []).map((child) => inlineText(child)).join("");
}

function prefixLines(lines: string[], prefix: string): string[] {
  const indent = " ".repeat(prefix.length);

  return lines.map((line, i) => (i === 0 ? prefix : indent) + line);
}

function blockLines(node: JSONContent): string[] {
  const children = node.content ?? [];

  switch (node.type) {
    case "bulletList": {
      return children.flatMap((item) => prefixLines(blockLines(item), "- "));
    }
    case "heading":
    case "paragraph": {
      return inlineText(node).split("\n");
    }
    case "orderedList": {
      const start = Number(node.attrs?.start ?? 1);

      return children.flatMap((item, i) =>
        prefixLines(blockLines(item), `${start + i}. `),
      );
    }
    default: {
      return children.flatMap((child) => blockLines(child));
    }
  }
}

/** Serializes a tiptap document back to the brief's plain-text form. */
export function briefTextFromDoc(doc: JSONContent): string {
  return blockLines(doc).join("\n").trim();
}
